import { ReportDashboardLayout } from '@/Layouts/ReportDashboardLayout'
import VerticallySplitLayout from '@/Layouts/VerticallySplitLayout'
import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { GFPADetailsChart } from '@/components/GFPA/DetailsReport/GFPADetailsChart'
import { GFPADetailsTable } from '@/components/GFPA/DetailsReport/GFPADetailsTable'
import { GFPATopAndBottomFiveSecuritiesTable } from '@/components/GFPA/DetailsReport/GFPATopAndBottomFiveSecuritiesTable'
import { GFPADashboardNavigation } from '@/components/GFPA/GFPADashboardNavigation'
import GFPAHeader from '@/components/GFPA/GFPAHeader'
import {
  gfpaColumnFieldForTableChartLinkingValue,
  gfpaDetailsGroupsColumnsFields,
  gfpaSecurityDetailsDefaultColumnsFieldsToVisible,
  gfpaSecurityDetailsFieldsToHide,
  gfpaSecurityDetailsTopAndBottom5FieldsToVisible,
  gfpaTopAndBottomFiveSecuritiesFilterKey,
} from '@/constants/gfpa.constant'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppDispatch } from '@/hooks/use-app-dispatch'
import { useAppSelector } from '@/hooks/use-app-selector'
import { IGfpaDetailsDTO } from '@/interfaces/gfpaReport.interface'
import { IDetailsDataMap } from '@/interfaces/rvDetails.interface'
import {
  clearGFPADetails,
  fetchGFPADetailsAsync,
} from '@/states/axiomReport.slice'
import { transformChartData } from '@/utils/chart-utils/chartUtils'
import { reportDefaultColDef } from '@/utils/grid-utils/colDefs'
import { getTableColumns } from '@/utils/grid-utils/columnUtils'
import { getTopAndBottomFiveSecurities } from '@/utils/tableGridUtils'
import { ColDef } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export const GFPADetailsPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selectedReport = useAppSelector((s) => s.axiomReport.selectedReport)
  const showLoader = useAppSelector((s) => s.axiomReport.showLoader)
  const gfpaDetails = useAppSelector((s) => s.axiomReport.gfpaReport.details)
  const gfpaSummarySelectedSeriesName = useAppSelector(
    (s) => s.axiomReport.gfpaReport.selectedSummarySeriesName,
  )
  const [gridReference, setGridReference] = useState<AgGridReact>()
  const [detailsTableColumns, setDetailsTableColumns] = useState<ColDef[]>([])
  const [
    topAndBottomFiveSecuritiesColumns,
    setTopAndBottomFiveSecuritiesColumns,
  ] = useState<ColDef[]>([])
  const [topAndBottomFiveSecurities, setTopAndBottomFiveSecurities] = useState<{
    topFiveSecurities: IGfpaDetailsDTO[]
    bottomFiveSecurities: IGfpaDetailsDTO[]
  }>({ topFiveSecurities: [], bottomFiveSecurities: [] })
  const [gfpaDetailsTableData, setGfpaDetailsTableData] = useState<
    IGfpaDetailsDTO[]
  >([])
  const [gfpaDetailsChartData, setGfpaDetailsChartData] =
    useState<IDetailsDataMap>({} as IDetailsDataMap)
  const [expandedRowGroupInfo, setExpandedRowGroupInfo] = useState<{
    name: string
    level: number
  }>({ name: '', level: 0 })

  useEffect(() => {
    if (
      Object.keys(selectedReport).length &&
      (selectedReport.reportType === 'GFPA' || selectedReport.reportType === 'GFPAH')
    ) {
      dispatch(
        fetchGFPADetailsAsync(
          selectedReport.reportId,
          gfpaSummarySelectedSeriesName,
        ),
      )
    } else {
      navigate('/reports')
    }
  }, [selectedReport])

  useEffect(() => {
    // Guard: Only process when we have actual data
    if (!gfpaDetails || gfpaDetails.length === 0) {
      return
    }
    
    const gfpaDetailsColumnsFields = Object.keys(gfpaDetails[0] || {})

    const gfpaColumnsMetaData =
      sessionStorage.getItem(AxiomReportType.GFPA) &&
      JSON.parse(sessionStorage.getItem(AxiomReportType.GFPA) as string)

    const defaultColDef = getTableColumns(
      gfpaColumnsMetaData,
      gfpaSecurityDetailsDefaultColumnsFieldsToVisible,
      gfpaColumnFieldForTableChartLinkingValue,
      gfpaDetailsColumnsFields,
      gfpaSecurityDetailsFieldsToHide,
    )
    const topAndBottomSecuritiesColumns = getTableColumns(
      gfpaColumnsMetaData,
      gfpaSecurityDetailsTopAndBottom5FieldsToVisible,
      [],
      gfpaDetailsColumnsFields,
      gfpaSecurityDetailsFieldsToHide,
      { ...reportDefaultColDef, filter: false },
    )

    const topAndBottomFiveSecuritiesData = getTopAndBottomFiveSecurities(
      gfpaDetails,
      gfpaTopAndBottomFiveSecuritiesFilterKey,
    ) as {
      topFiveSecurities: IGfpaDetailsDTO[]
      bottomFiveSecurities: IGfpaDetailsDTO[]
    }
    const finalColDefs = [...defaultColDef]
    setDetailsTableColumns(finalColDefs)
    const pieChartData = transformChartData(
      gfpaDetails,
      gfpaDetailsGroupsColumnsFields,
      gfpaColumnFieldForTableChartLinkingValue[0],
      'Notional Weight',
    )
    
    setGfpaDetailsTableData(gfpaDetails)
    setGfpaDetailsChartData(pieChartData)
    setTopAndBottomFiveSecuritiesColumns(topAndBottomSecuritiesColumns)
    setTopAndBottomFiveSecurities(topAndBottomFiveSecuritiesData)
  }, [gfpaDetails])

  useEffect(() => {
    return () => {
      dispatch(clearGFPADetails())
    }
  }, [])

  const handleExpandRowGroup = (info: { name: string; level: number }) => {
    setExpandedRowGroupInfo((prev) => ({
      ...prev,
      name: info.name,
      level: info.level,
    }))
  }
  const handleColumnRowGroupSelectionChanged = (
    groupColumnFieldName: string,
  ) => {
    const columnGroupName = groupColumnFieldName ? [groupColumnFieldName] : []
    const pieChartData = transformChartData(
      gfpaDetails,
      columnGroupName,
      gfpaColumnFieldForTableChartLinkingValue[0],
      'Notional Weight',
    )
    setGfpaDetailsChartData(pieChartData)
  }

  return (
    <ReportDashboardLayout
      selectedReport={selectedReport}
      navigation={<GFPADashboardNavigation />}
    >
      {showLoader ? (
        <LoadingCard />
      ) : (
        <VerticallySplitLayout
          contentTop={
            gfpaDetailsTableData.length > 0 ? (
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <GFPADetailsChart
                    data={gfpaDetailsChartData}
                    expandedRowGroupInfo={expandedRowGroupInfo}
                    reportType={selectedReport.reportType}
                    gfpaSummarySelectedSeriesName={
                      gfpaSummarySelectedSeriesName
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <GFPATopAndBottomFiveSecuritiesTable
                    securitiesDetailsColDef={topAndBottomFiveSecuritiesColumns}
                    securitiesData={topAndBottomFiveSecurities}
                    reportType={selectedReport.reportType}
                  />
                </Grid>
              </Grid>
            ) : (
              <NoDataToDisplayCard />
            )
          }
          contentBottom={
            gfpaDetailsTableData.length ? (
              <GFPADetailsTable
                reportType={selectedReport.reportType}
                rowData={gfpaDetailsTableData}
                setDetailsTableColumns={setDetailsTableColumns}
                setGridReference={setGridReference}
                detailsColDef={detailsTableColumns}
                onExpandRowGroup={handleExpandRowGroup}
                onColumnRowGroupSelectionChanged={
                  handleColumnRowGroupSelectionChanged
                }
              />
            ) : (
              <NoDataToDisplayCard />
            )
          }
          headerBottom={
            <GFPAHeader
              reportData={selectedReport}
              gridReference={gridReference as AgGridReact}
            />
          }
        />
      )}
    </ReportDashboardLayout>
  )
}
